import React, { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import { useSelector } from "react-redux";
import { check, PERMISSIONS, RESULTS, request } from "react-native-permissions";
import {
  isNativeCodeScannerAvailable,
  scanCode,
  scannerConfigForFlow,
} from "@utils/nativeCodeScanner";
import CrateScannerSheet from "./CrateScannerSheet";
import { notifyError } from "./notify";

// Opens the native full-screen QR / barcode scanner
// (react-native-ops-scanner) while `visible` is true. One code per open: the
// parent gets it via onDecode, a back / close press via onClose. The scan mode
// (QR, BARCODE or the QR | Barcode switcher) comes from setup-4
// `scannerModes[flow]`.
//
// Falls back to the in-app CrateScannerSheet when the binary has no native
// module (CodePush can ship this JS to an older APK) or the camera can't start.

const requestCamera = async () => {
  try {
    const perm =
      Platform.OS === "ios"
        ? PERMISSIONS.IOS.CAMERA
        : PERMISSIONS.ANDROID.CAMERA;
    const status = await check(perm);
    if (status === RESULTS.GRANTED) return true;
    const next = await request(perm);
    return next === RESULTS.GRANTED;
  } catch {
    return false;
  }
};

const NativeScannerSheet = ({ visible, onClose, onDecode, flow, title }) => {
  const scannerModes = useSelector(
    ({ configReducer }) => configReducer?.scannerModes,
  );
  const [useFallback, setUseFallback] = useState(
    () => !isNativeCodeScannerAvailable(),
  );
  // Latest callbacks without re-launching the scanner when a parent re-renders.
  const handlers = useRef({ onClose, onDecode });
  handlers.current = { onClose, onDecode };

  useEffect(() => {
    if (!visible || useFallback) return undefined;
    let cancelled = false;
    (async () => {
      if (!(await requestCamera())) {
        notifyError("Camera permission denied");
        handlers.current.onClose?.();
        return;
      }
      try {
        const result = await scanCode({
          ...scannerConfigForFlow(scannerModes, flow),
          title: title || null,
        });
        if (cancelled) return;
        if (!result.didCancel && result.value) {
          handlers.current.onDecode?.(String(result.value).trim());
        } else {
          handlers.current.onClose?.();
        }
      } catch {
        // Native screen couldn't run (camera / ML Kit init): old scanner.
        if (!cancelled) setUseFallback(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, useFallback]);

  if (!useFallback) return null;
  return (
    <CrateScannerSheet
      visible={visible}
      onClose={onClose}
      onDecode={onDecode}
    />
  );
};

export default NativeScannerSheet;
